<template>
    <div class="success-celebration" aria-hidden="true">
        <div class="success-celebration__scaler">
            <div class="success-celebration__stage">
                <span v-for="(circle, i) in CIRCLES" :key="i" :ref="(el) => setBlobRef(el, i)" class="success-celebration__blob" :style="{ background: circle.colour }"></span>
            </div>
            <div ref="check" class="success-celebration__check">
                <svg aria-hidden="true" viewBox="0 0 40 30" fill="none" stroke="currentColor" stroke-width="4.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M4 16 L15 26 L36 4" />
                </svg>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import launchConfetti from "canvas-confetti";
import { computed, onMounted, onUnmounted, ref } from "vue";

// A one-shot "success moment" animation: five brand circles converge and collapse into a
// checkmark badge, with a confetti burst. Choreography is ported from the "Charles Fun Play"
// V4 ("Sped up") prototype. Auto-plays on mount; call replay() to run it again.

interface Props {
    // Fire the canvas-confetti burst when the badge lands. Defaults on at full size and off when
    // `size` is set (a window-wide burst over a small inline icon is jarring). Pass to override.
    confetti?: boolean;
    // Rendered square size in px. null (default) = fill width at the base 240px height (dialog usage).
    size?: number | null;
}
const props = withDefaults(defineProps<Props>(), { size: null });

// The choreography is authored in a 240px square; a CSS transform scales the whole scene to `size`.
const BASE_PX = 240;
const boxWidth = computed(() => (props.size != null ? `${props.size}px` : "100%"));
const boxHeight = computed(() => (props.size != null ? `${props.size}px` : `${BASE_PX}px`));
const contentScale = computed(() => (props.size != null ? props.size / BASE_PX : 1));
const confettiEnabled = computed(() => props.confetti ?? props.size == null);

// Resting angle around the ring (deg, 0 = right, y down) + colour for each circle. These are
// prototype values with no design-system token yet.
const CIRCLES = [
    { colour: "#84D6D0", angle: -90 }, // teal   — enters from top
    { colour: "#B92A6A", angle: -18 }, // pink   — enters from upper-right
    { colour: "#FBB877", angle: 54 }, // orange — enters from lower-right
    { colour: "#BBE3B4", angle: 126 }, // green  — enters from lower-left
    { colour: "#7E78DE", angle: 198 }, // purple — enters from left
] as const;

interface Keyframe {
    t: number; // seconds (before TIME_SCALE)
    R: number; // distance from centre (px)
    size: number; // diameter (px)
    spin: number; // shared rotation (deg)
    op: number; // opacity
}

// entrance → loose ring → converge/overlap → tight twirl → collapse into the check.
const KF: Keyframe[] = [
    { t: 0.0, R: 150, size: 46, spin: 0, op: 0 },
    { t: 0.32, R: 84, size: 62, spin: 20, op: 1 },
    { t: 0.92, R: 56, size: 74, spin: 150, op: 1 },
    { t: 1.52, R: 32, size: 84, spin: 330, op: 1 },
    { t: 2.05, R: 0, size: 42, spin: 430, op: 0 },
];
const BADGE_AT = 1.85; // when the check pops in
const DUR = KF[KF.length - 1].t;
const TIME_SCALE = 1.7; // V4 — "Sped up"
const DEG = Math.PI / 180;

const check = ref<HTMLDivElement | null>(null);
const blobEls: HTMLElement[] = [];
const setBlobRef = (el: unknown, i: number) => {
    if (el instanceof HTMLElement) {
        blobEls[i] = el;
    }
};

let rafId: number | null = null;
let startTime: number | null = null;
let badgeShown = false;

// Catmull-Rom / Hermite spline over the keyframes: tangents are shared across keyframe
// boundaries so velocity stays continuous — one flowing gesture instead of stops and restarts.
const T = KF.map((k) => k.t);
const series = (key: keyof Keyframe): number[] => KF.map((k) => k[key]);
const R_SERIES = series("R");
const SIZE_SERIES = series("size");
const SPIN_SERIES = series("spin");
const OP_SERIES = series("op");

function tangent(a: number[], i: number): number {
    if (i === 0) {
        return (a[1] - a[0]) / (T[1] - T[0]);
    }
    if (i === a.length - 1) {
        return 0;
    }
    return (a[i + 1] - a[i - 1]) / (T[i + 1] - T[i - 1]);
}

function spline(a: number[], time: number): number {
    if (time <= T[0]) {
        return a[0];
    }
    if (time >= T[T.length - 1]) {
        return a[a.length - 1];
    }
    let i = 0;
    while (i < T.length - 1 && time > T[i + 1]) {
        i++;
    }
    const h = T[i + 1] - T[i];
    const s = (time - T[i]) / h;
    const s2 = s * s;
    const s3 = s2 * s;
    const m0 = tangent(a, i) * h;
    const m1 = tangent(a, i + 1) * h;
    return (2 * s3 - 3 * s2 + 1) * a[i] + (s3 - 2 * s2 + s) * m0 + (-2 * s3 + 3 * s2) * a[i + 1] + (s3 - s2) * m1;
}

function sample(time: number) {
    return {
        R: spline(R_SERIES, time),
        size: spline(SIZE_SERIES, time),
        spin: spline(SPIN_SERIES, time),
        op: Math.max(0, Math.min(1, spline(OP_SERIES, time))),
    };
}

function prefersReducedMotion(): boolean {
    return typeof globalThis.matchMedia === "function" && globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function fireConfetti() {
    if (!confettiEnabled.value) {
        return;
    }
    let origin = { x: 0.5, y: 0.45 };
    const el = check.value;
    if (el && globalThis.innerWidth && globalThis.innerHeight) {
        const rect = el.getBoundingClientRect();
        origin = {
            x: (rect.left + rect.width / 2) / globalThis.innerWidth,
            y: (rect.top + rect.height / 2) / globalThis.innerHeight,
        };
    }
    launchConfetti({
        particleCount: 50,
        spread: 360, // full radial burst from the centre (not an upward cone)
        startVelocity: 18, // small explosion radius, matching the prototype
        ticks: 90,
        gravity: 1.0,
        scalar: 1.5, // chunkier particles
        decay: 0.9,
        colors: CIRCLES.map((c) => c.colour), // reuse the circle palette
        shapes: ["square", "circle"], // prototype's squares + dots
        disableForReducedMotion: true,
        origin,
    });
}

function frame(now: number) {
    if (startTime === null) {
        startTime = now;
    }
    const t = ((now - startTime) / 1000) * TIME_SCALE;
    const s = sample(t);
    for (let i = 0; i < blobEls.length; i++) {
        const b = blobEls[i];
        if (!b) {
            continue;
        }
        const th = (CIRCLES[i].angle + s.spin) * DEG;
        const x = Math.cos(th) * s.R;
        const y = Math.sin(th) * s.R;
        b.style.width = `${s.size}px`;
        b.style.height = `${s.size}px`;
        b.style.opacity = String(s.op);
        b.style.transform = `translate(-50%, -50%) translate(${x.toFixed(1)}px, ${y.toFixed(1)}px)`;
    }
    if (!badgeShown && t >= BADGE_AT) {
        badgeShown = true;
        check.value?.classList.add("is-shown");
        fireConfetti();
    }
    if (t < DUR) {
        rafId = requestAnimationFrame(frame);
    }
}

function play() {
    if (rafId !== null) {
        cancelAnimationFrame(rafId);
    }
    startTime = null;
    badgeShown = false;
    check.value?.classList.remove("is-shown", "is-static");
    // Reduced motion: skip the choreography, just present the finished check.
    if (prefersReducedMotion()) {
        check.value?.classList.add("is-static");
        return;
    }
    rafId = requestAnimationFrame(frame);
}

function replay() {
    play();
}
defineExpose({ replay });

onMounted(play);
onUnmounted(() => {
    if (rafId !== null) {
        cancelAnimationFrame(rafId);
    }
});
</script>

<style scoped>
.success-celebration {
    position: relative;
    width: v-bind(boxWidth);
    height: v-bind(boxHeight);
    overflow: hidden;
}

/* Base 240px coordinate space the choreography is authored in; scaled uniformly to fit `size`. */
.success-celebration__scaler {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 240px;
    height: 240px;
    transform: translate(-50%, -50%) scale(v-bind(contentScale));
    transform-origin: center;
}

/* Circles are centred here and offset via transform each frame. */
.success-celebration__stage {
    position: absolute;
    inset: 0;
    pointer-events: none;
}

/* multiply blend gives the overlap mixing as the circles converge. */
.success-celebration__blob {
    position: absolute;
    left: 50%;
    top: 50%;
    border-radius: 50%;
    mix-blend-mode: multiply;
    will-change: transform, width, height, opacity;
}

/* Raspberry check the circles collapse into (prototype colour, no token yet). */
.success-celebration__check {
    position: absolute;
    left: 50%;
    top: 50%;
    z-index: 5;
    width: 112px;
    height: 112px;
    border-radius: 50%;
    background: #b92a6a;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 12px 34px rgba(185, 42, 106, 0.34);
    transform: translate(-50%, -50%) scale(0);
    opacity: 0;
}
.success-celebration__check svg {
    width: 46px;
    height: 34px;
}
.success-celebration__check svg path {
    stroke-dasharray: 48;
    stroke-dashoffset: 48;
}
.success-celebration__check.is-shown {
    animation: sc-badge-pop 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}
.success-celebration__check.is-shown svg path {
    animation: sc-check-draw 0.35s ease-out 0.28s forwards;
}
/* Reduced-motion / static: the check is simply present. */
.success-celebration__check.is-static {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
}
.success-celebration__check.is-static svg path {
    stroke-dashoffset: 0;
}

@keyframes sc-badge-pop {
    0% {
        transform: translate(-50%, -50%) scale(0);
        opacity: 0;
    }
    70% {
        transform: translate(-50%, -50%) scale(1.12);
        opacity: 1;
    }
    100% {
        transform: translate(-50%, -50%) scale(1);
        opacity: 1;
    }
}
@keyframes sc-check-draw {
    to {
        stroke-dashoffset: 0;
    }
}

@media (prefers-reduced-motion: reduce) {
    .success-celebration__blob {
        display: none;
    }
    .success-celebration__check {
        transform: translate(-50%, -50%) scale(1);
        opacity: 1;
    }
    .success-celebration__check svg path {
        stroke-dashoffset: 0;
    }
}
</style>
